import { useCentralApi, useCurrentUser, useFeatureEnabled } from "hooks/shared";
import useTaskContext from "./useTaskContext";
import { useState } from "react";
import { ICompleteOptions, IProcessTimeOptions, ITaskOverview } from "models/tasks";
import ProcessTimeDialog from "components/tasks/process-time";
import { useSnackbar } from "@sagecentral/core-ui";
import { ValidationSummary } from "components/shared";

type TaskCompleteHandler = (confirm?: boolean, options?: ICompleteOptions) => void;
type OnCompleteSignature = (data: ITaskOverview) => Promise<void>;
type OnFailedSignature = (message: string) => void;
type UseTaskCompleteResult = [
    TaskCompleteHandler,
    boolean,
    React.ReactNode
];

type UseTaskCompleteSignature = (onCompleted?: OnCompleteSignature, onFailed?: OnFailedSignature) => UseTaskCompleteResult;

const useTaskComplete: UseTaskCompleteSignature = (onCompleted, onFailed) => {
    const taskContext = useTaskContext();
    const { displaySnackbar } = useSnackbar();     
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const [ processTimeEnabled ] = useFeatureEnabled('process-time');

    const { task } = taskContext;

    const [ working, setWorking ] = useState<boolean>(false);
    const [ showDialog, setShowDialog ] = useState<boolean>(false);
    
    const handleComplete = async (confirm?: boolean, options?: ICompleteOptions) => {
        if (task === null) {
            console.warn('no task exists in handleComplete');
            return;
        }

        if (!confirm && processTimeEnabled && task.requireProcessTime) {
            setShowDialog(true);
            return;
        }

        setWorking(true);
        try {
            const resp = await fetcher(`/api/v3/tasks/${task.id}/complete`, {
                method: 'PATCH',
                body: JSON.stringify(options ?? {})
            }, async r => {
                if (r.status === 400) {
                    const data = await r.json();
                    displaySnackbar('warning', <ValidationSummary validationDetail={data} />);
                } else {
                    return false;
                }
                return true;
            });

            const data = await resp.json();
            if (!resp.ok) {
                if (resp.status === 400) {
                    const error = data.errors['*'];
                    if (onFailed) {
                        onFailed(error);
                    }
                }
            } else {
                if (onCompleted) {
                    onCompleted(data as ITaskOverview);
                }
            }
        } finally {
            setWorking(false);
        }
    };

    const handleConfirm = async (options: IProcessTimeOptions) => {
        await handleComplete(true, {
            processTime: options
        });
        setShowDialog(false);
    };

    const Dialog = (
        <ProcessTimeDialog open={showDialog} working={working}
            onCancel={() => setShowDialog(false)}
            onConfirm={handleConfirm}
        />
    );

    return [
        handleComplete,
        working,
        Dialog
    ];
};

export default useTaskComplete;
